/**
 * Script-handler registry root.
 *
 * Imported once at app boot (from main.ts) so handler files run their
 * registerScript() calls before any trigger / dialogue dispatch fires.
 * Side-effect-only import: handlers register themselves via this file's
 * top-level statements.
 *
 * Adding a new handler:
 *   1. Create scripts/handlers/<myHandler>.ts exporting an array (or
 *      single entry) of `{ scriptRef, handler }`.
 *   2. Import + iterate-register here.
 *   3. The handler now dispatches via scriptRunner.dispatchScript() the
 *      next time a matching scriptRef hits the dialogue-close path or
 *      coord-event dispatcher.
 */
import { registerScript, registerSyntheticHandler } from '../scriptRunner';
import { ITEM_BALLS } from '../../data/items';
import { itemBallPickupHandler } from './itemBallPickup';
import { DAISY_SCRIPT_REF, daisyTownMapHandler } from './daisyTownMap';
import { oakInterceptHandler } from './oakIntercept';
import { LAB_STARTER_BALL_HANDLERS } from './labStarterBall';
import { rivalLabHandler, rivalPostBattleHandler, RIVAL_LAB_TRIGGER_REFS } from './rivalLab';
import { labOakTapHandler } from './labOakTap';
import { labRivalTapHandler } from './labRivalTap';
import { labLeaveGuardHandler, LAB_LEAVE_GUARD_TRIGGER_REFS } from './labLeaveGuard';
import { labPostPickHandler } from './labPostPick';

// G3: item-ball pickups — one handler over the ITEM_BALLS table. The
// tap shows the "You found X!" dialogue; on close the handler grants
// the item + hides the ball + saves.
for (const ref of Object.keys(ITEM_BALLS)) {
  registerScript(ref, itemBallPickupHandler);
}

// G9: DAISY grants the TOWN MAP on first talk (cartridge acquisition
// path; the map-object in her room stays permanently hidden).
registerScript(DAISY_SCRIPT_REF, daisyTownMapHandler);

// Oak intercept on Route 1 — both flanking trigger tiles fire the same
// handler. cartridge differentiates LEFT vs RIGHT for the Oak-walks-
// down path geometry; poke's handler computes oakStart from the
// player's tile at fire time, so the same handler handles both.
registerScript('PalletTown_EventScript_OakTriggerLeft', oakInterceptHandler);
registerScript('PalletTown_EventScript_OakTriggerRight', oakInterceptHandler);

// Starter ball-tap handlers (3 — one per species).
for (const { scriptRef, handler } of LAB_STARTER_BALL_HANDLERS) {
  registerScript(scriptRef, handler);
}

// Rival lab cinematic — 3 coord-event triggers at row 8 (Left/Mid/Right
// columns 5/6/7) all route to the same handler. The cartridge var-gate
// on VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB == 3 is set by
// DraftScene.commit's Oak's-Lab branch.
for (const ref of RIVAL_LAB_TRIGGER_REFS) {
  registerScript(ref, rivalLabHandler);
}

// PR A1: state-aware NPC tap dispatchers. Replace the static dialogue.ts
// entries for the in-lab Oak + Rival NPCs with handlers that read the
// labScene state machine and return cartridge-appropriate lines per
// phase. Refs match runtime.json `scriptRef` fields on the NPC objects.
registerScript('PalletTown_ProfessorOaksLab_EventScript_ProfOak', labOakTapHandler);
registerScript('PalletTown_ProfessorOaksLab_EventScript_Rival', labRivalTapHandler);

// PR A3: leave-guard for player attempting to leave the lab pre-pick.
// The 3 row-8 LeaveStarterScene triggers (varValue=2) all share the
// same scriptRef in runtime.json, so a single registration covers them.
for (const ref of LAB_LEAVE_GUARD_TRIGGER_REFS) {
  registerScript(ref, labLeaveGuardHandler);
}

// PR A3: synthetic post-pick cinematic. Dispatched from WorldScene's
// WAKE handler when STARTER_PICKED is set + state==2. The handler
// orchestrates Oak's confirm/advice + rival walks to opposing ball.
registerSyntheticHandler('__lab_post_pick__', labPostPickHandler);

// PR A4: synthetic rival post-battle cinematic. Dispatched from WAKE
// handler when RIVAL_BATTLE_RESOLVED is set (BattleScene flips it on
// p1 victory over a rival ref) AND RIVAL_POST_BATTLE_DONE is unset.
// The handler re-spawns the walker at VAR_RIVAL_LAST_TILE, runs the
// "Smell you later!" dialogue + walk-to-door, advances state→4.
registerSyntheticHandler('__rival_post_battle__', rivalPostBattleHandler);
